import { FunctionComponent, useEffect, useState } from "react";
import { Box, HStack, Icon, IconButton, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Text, Tooltip, useToast } from "@chakra-ui/react";
import { IconCopy, IconDownload, IconExternalLink, IconQrcode } from "@tabler/icons-react";
import { useCandidateUrl } from "../../../../api/hooks/useCandidateUrl";
import { useCandidateQr } from "../../../../api";
import { Shimmer } from "@am-tax/fe-core";

/**
 * Compact survey link bar — shows the candidate questionnaire URL with
 * open-in-tab, copy, and QR-code-popover actions.
 * Intended to be embedded directly in the entity info card.
 */
export const SurveyLinkSection: FunctionComponent<{ clientId: string; entityId: string }> = ({ clientId, entityId }) => {
    const toast = useToast();
    const candidateUrlQuery = useCandidateUrl(clientId, entityId);
    const candidateQrQuery = useCandidateQr(clientId, entityId);
    const [qrUrl, setQrUrl] = useState<string>();
    const surveyUrl = candidateUrlQuery.data;
    const isLoading = candidateUrlQuery.isLoading;

    useEffect(() => {
        if (candidateQrQuery.data && !qrUrl) {
            const blob = new Blob([candidateQrQuery.data]);
            setQrUrl(URL.createObjectURL(blob));
        }
    }, [candidateQrQuery.data, qrUrl]);

    const copy = () => {
        navigator.clipboard.writeText(surveyUrl?.url ?? "");
        toast({ title: "Copied to clipboard", status: "success", duration: 2000, isClosable: true });
    };

    const openInNewTab = () => {
        window.open(surveyUrl?.url ?? "", "_blank")?.focus();
    };

    const downloadQr = () => {
        if (!qrUrl) return;
        const a = document.createElement("a");
        a.href = qrUrl;
        a.download = `qr-${entityId}.png`;
        a.click();
    };

    return (
        <Box>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" color="gray.500" mb={2}>
                Candidate Questionnaire
            </Text>
            <Shimmer isLoading={isLoading} width="100%" height={40}>
                <HStack bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" px="0.75rem" py="0.4rem" spacing="0.25rem">
                    <Text
                        flex={1}
                        fontSize="sm"
                        color="gray.600"
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        cursor="pointer"
                        onClick={openInNewTab}
                        _hover={{ color: "blue.500" }}
                    >
                        {surveyUrl?.url}
                    </Text>

                    <Tooltip label="Open in new tab">
                        <IconButton aria-label="Open survey link" icon={<Icon as={IconExternalLink} />} size="sm" variant="ghost" onClick={openInNewTab} />
                    </Tooltip>

                    <Tooltip label="Copy URL">
                        <IconButton aria-label="Copy URL" icon={<Icon as={IconCopy} />} size="sm" variant="ghost" onClick={copy} />
                    </Tooltip>

                    <Popover placement="bottom-end" isLazy>
                        <Tooltip label="Show QR code">
                            <Box display="inline-flex">
                                <PopoverTrigger>
                                    <IconButton aria-label="Show QR code" icon={<Icon as={IconQrcode} />} size="sm" variant="ghost" isDisabled={!qrUrl} />
                                </PopoverTrigger>
                            </Box>
                        </Tooltip>
                        <PopoverContent width="280px">
                            <PopoverArrow />
                            <PopoverBody p="1rem">
                                <img src={qrUrl} alt={surveyUrl?.url} style={{ width: "100%", height: "auto" }} />
                                <Tooltip label="Download QR code">
                                    <IconButton
                                        aria-label="Download QR code"
                                        icon={<Icon as={IconDownload} />}
                                        size="sm"
                                        variant="ghost"
                                        width="100%"
                                        mt="0.5rem"
                                        onClick={downloadQr}
                                    />
                                </Tooltip>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                </HStack>
            </Shimmer>
        </Box>
    );
};
